// @ts-nocheck
import React, { useEffect, useRef, useState } from 'react';
import FormStatic from '../../common/FormStatic';
import KwPanel from '../../common/KwPanel';
import SlideoutLayout from '../../layout/slideout/SlideoutLayout';
import FormInput from '../../common/FormInput';
import FormDataList from '../../common/FormDataList';
import {
  saveRequest,
  clearSteamRequest,
} from '../../../actions/steamRequestActions';
import { getAllPackageTypes } from '../../../actions/packageTypeActions';
import { toast } from 'react-toastify';
import { useForm } from 'react-hook-form';
import Loading from '../../common/Loading';
import { steamRequestKeysUri } from '../../../util/steamUtils';
import Button from '../../common/Button';
import FormSelect from '../../common/FormSelect';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import {
  addToGroup,
  removeFromGroup,
} from '../../../middleware/signalRMiddleware';
import FormTextArea from '../../common/FormTextArea';
import { getAllWorkflows } from '../../../actions/workflowActions';

export interface SteamRequestProps {
  done: () => void;
  cancel: () => void;
  keyWorkflowId?: string;
  keyPoolName: string;
  gameName: string;
  gameSkuId: string;
  skuName: string;
  keyRequestId?: string;
  customerName: string;
  requesterTag?: string;
  packageTypeId?: string;
  numberOfKeys?: number;
}

function SteamRequest({
  done,
  cancel,
  keyWorkflowId,
  keyPoolName,
  gameName,
  gameSkuId,
  skuName,
  keyRequestId,
  customerName,
  requesterTag,
  packageTypeId,
  numberOfKeys,
}: SteamRequestProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const steamRequest = useAppSelector((state) => state.steamRequest);
  const packageTypes = useAppSelector((state) => state.packageTypes);
  const workflows = useAppSelector((state) => state.workflows);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();
  const [loading, setLoading] = useState(false);
  const [steamRequestId, setSteamRequestId] = useState();
  let timer = useRef();

  const onError = (message) => {
    toast.error(message);
    setLoading(false);
  };

  const onSubmit = (record) => {
    setLoading(true);
    saveRequest({ keyWorkflowId, gameSkuId, keyRequestId, ...record })
      .then((data) => {
        setSteamRequestId(data.id);
        //uri schema
        window.location = steamRequestKeysUri(data.id);
        timer.current = setTimeout(() => {
          onError(t('Failed to launch Steam request within 30 seconds'));
        }, 30000);
      })
      .catch((e) => {
        setLoading(false);
        toast.error(e?.message ?? t('Failed to create Steam request'));
      });
  };

  useEffect(() => {
    if (steamRequestId) {
      dispatch(addToGroup(`steamRequest:${steamRequestId}`));
      return () => {
        dispatch(removeFromGroup(`steamRequest:${steamRequestId}`));
        clearTimeout(timer.current);
      };
    }
  }, [steamRequestId, dispatch]);

  useEffect(() => {
    if (steamRequest && steamRequest.id === steamRequestId) {
      dispatch(clearSteamRequest());
      clearTimeout(timer.current);
      if (steamRequest.hasErrored) {
        onError(steamRequest.errorMessage);
      } else if (steamRequest.hasCompleted) {
        toast.success(t('Steam request successful'));
        done();
      }
    }
  }, [steamRequestId, steamRequest, dispatch, done, t]);

  useEffect(() => {
    if (!packageTypes) {
      dispatch(getAllPackageTypes());
    }
  }, [packageTypes, dispatch]);

  useEffect(() => {
    if (!workflows) {
      dispatch(getAllWorkflows());
    }
  }, [workflows, dispatch]);

  const getCanEditNumberOfKeys = () => {
    const keyWorkflow = workflows?.find((x) => x.id === keyWorkflowId);
    return keyWorkflow?.enableKeyPools ?? false;
  };

  //this is duplicated from SteamBulkRequest.js
  const getTags = (keyWorkflowId) => {
    const keyWorkflow = workflows?.find((x) => x.id === keyWorkflowId);
    return keyWorkflow?.requesterTags?.map((x) => x.tag) || [];
  };

  return (
    <SlideoutLayout
      title={t('Request on Steam')}
      cancel={cancel}
      bar={
        !loading && (
          <Button
            className="btn btn-primary"
            onClick={handleSubmit(onSubmit)}
            text={t('Request on Steam')}
          />
        )
      }
    >
      {!loading ? (
        <KwPanel className="mb-4 form-horizontal">
          <FormStatic label={t('Key pool')}>{keyPoolName}</FormStatic>
          <FormStatic label={t('Game')}>{gameName}</FormStatic>
          <FormStatic label={t('SKU')}>{skuName}</FormStatic>
          <FormStatic label={t('Requester tag')}>{requesterTag || '-'}</FormStatic>
          <FormSelect
            register={register}
            name="packageTypeId"
            label={t('Package type')}
            error={errors.packageTypeId}
            errorMessage={t('Package type is required')}
            required
            autoFocus={true}
            defaultValue={packageTypeId || ''}
          >
            <option value="">{t('--- choose ---')}</option>
            {packageTypes &&
              [...packageTypes]
                ?.sort((a, b) => a.name.localeCompare(b.name))
                .map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.name}
                  </option>
                ))}
          </FormSelect>
          <FormDataList
            register={register}
            name="keyProviderReference"
            label={t('Customer')}
            required
            disabled={loading}
            items={getTags(keyWorkflowId)}
            defaultValue={customerName || ''}
          />
          <FormInput
            register={register}
            name="numberOfKeys"
            label={t('Number of keys')}
            error={errors.numberOfKeys}
            errorMessage={t('Number of keys is required')}
            required
            autoFocus={true}
            defaultValue={numberOfKeys || ''}
            disabled={!getCanEditNumberOfKeys()}
            title={
              !getCanEditNumberOfKeys()
                ? t('Key pool is not enabled on this workflow')
                : ''
            }
          ></FormInput>
          <FormTextArea
            register={register}
            name="comment"
            label={t('Comment (Optional)')}
            autoFocus={true}
            defaultValue={''}
          />
        </KwPanel>
      ) : (
        <Loading></Loading>
      )}
    </SlideoutLayout>
  );
}

export default SteamRequest;
